import { useQueries } from '@tanstack/react-query';
import { MountPoint, Volume } from 'docker-types';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { buildDockerProxyUrl } from '@/react/docker/proxy/queries/buildDockerProxyUrl';
import { withAgentTargetHeader } from '@/react/docker/proxy/queries/utils';
import { queryKeys } from '@/react/docker/volumes/queries/query-keys';

/**
 * Inspects the named volumes mounted in a container.
 * Volumes the user cannot inspect are left out of the result.
 */
export function useMountedVolumes({
  environmentId,
  nodeName,
  mounts = [],
  enabled,
}: {
  environmentId?: EnvironmentId;
  nodeName?: string;
  mounts?: Array<MountPoint>;
  enabled: boolean;
}) {
  const names = mounts.flatMap((mount) =>
    mount.Type === 'volume' && mount.Name ? [mount.Name] : []
  );

  const queries = useQueries({
    queries: names.map((name) => ({
      queryKey: [
        ...queryKeys.base(environmentId ?? 0),
        name,
        'inspect',
        nodeName,
      ],
      queryFn: () => getVolume(environmentId ?? 0, name, nodeName),
      enabled: enabled && !!environmentId,
      retry: false,
    })),
  });

  return {
    isLoading: queries.some((query) => query.isInitialLoading),
    volumes: queries.flatMap((query) => (query.data ? [query.data] : [])),
  };
}

async function getVolume(
  environmentId: EnvironmentId,
  name: string,
  nodeName?: string
) {
  try {
    const { data } = await axios.get<Volume>(
      buildDockerProxyUrl(environmentId, 'volumes', name),
      { headers: { ...withAgentTargetHeader(nodeName) } }
    );
    return data;
  } catch (e) {
    throw parseAxiosError(e, 'Unable to retrieve volume details');
  }
}
